<script setup>
/**
 * PRACTICE, AND THE WAY OUT OF IT (ease package P-5.2).
 *
 * A new account starts in practice: the assistant answers only Test it and the owner's test
 * numbers, and every other chat goes to her team as a normal open conversation, free. This
 * card is what stands on Overview while that is true: three ticks, each with a link to where
 * it is done; the test numbers; and one button, "Start answering customers".
 *
 * O-1: THE BUTTON IS NEVER DISABLED. A confirm names what is left ("Not done yet: try it.
 * Start answering every customer anyway?"), because she may know better than three ticks do.
 * Going live is a session action: the BFF records who pressed it, and the Claude connector
 * has no route to it.
 */
import { computed, ref } from 'vue';

import Button from 'dashboard/components-next/button/Button.vue';
import SettingsHeader from 'dashboard/components-next/captain/pageComponents/settings/SettingsHeader.vue';
import { frontendURL } from 'dashboard/helper/URLHelper';

import TestNumbersEditor from './outbound/TestNumbersEditor.vue';
import { useWasloI18n } from '../i18n.js';
import { wasloApi } from '../api/client.js';
import { READINESS_MAX_TESTERS } from '../helpers/status.js';

const props = defineProps({
  /** The whole status (`wasloApi.getStatus`): readiness, testers, account. */
  status: { type: Object, required: true },
  accountId: { type: Number, default: null },
});

/** The new status, after any write here. The page renders from it. */
const emit = defineEmits(['changed']);

const { t } = useWasloI18n();

const STEPS = [
  { key: 'described', copy: 'DESCRIBED', path: 'waslo/personality' },
  { key: 'knows', copy: 'KNOWS', path: 'waslo/answers' },
  { key: 'tried', copy: 'TRIED', path: 'waslo/test' },
];

const steps = computed(() =>
  STEPS.map(step => ({
    ...step,
    done: props.status.readiness?.[step.key] === true,
    to:
      props.accountId === null
        ? null
        : frontendURL(`accounts/${props.accountId}/${step.path}`),
    // P-8.1: describing the business is the one step Waslo can draft for her.
    draftTo:
      step.key === 'described' && props.accountId !== null
        ? frontendURL(
            `accounts/${props.accountId}/waslo/personality/first-draft`
          )
        : null,
  }))
);
const left = computed(() => steps.value.filter(step => !step.done));

// ── the test numbers ─────────────────────────────────────────────────────────────
const testers = computed(() => props.status.testers ?? []);
const savingTesters = ref(false);
const testersError = ref('');

const writeTesters = async phones => {
  if (props.accountId === null || savingTesters.value) return;
  savingTesters.value = true;
  testersError.value = '';
  try {
    emit('changed', await wasloApi.updateTesters(props.accountId, phones));
  } catch {
    testersError.value = t('WASLO.READINESS.TESTERS_FAILED');
  } finally {
    savingTesters.value = false;
  }
};
const addTester = number => writeTesters([...testers.value, number]);
const removeTester = number =>
  writeTesters(testers.value.filter(item => item !== number));

// ── going live ─────────────────────────────────────────────────────────────────────
const confirming = ref(false);
const going = ref(false);
const goError = ref('');

const confirmLine = computed(() =>
  left.value.length === 0
    ? t('WASLO.READINESS.CONFIRM_READY')
    : t('WASLO.READINESS.CONFIRM_LEFT', {
        steps: left.value
          .map(step => t(`WASLO.READINESS.LEFT.${step.copy}`))
          .join(t('WASLO.COMMON.LIST_SEPARATOR')),
      })
);

const askGoLive = () => {
  goError.value = '';
  confirming.value = true;
};

const goLive = async () => {
  if (props.accountId === null || going.value) return;
  going.value = true;
  goError.value = '';
  try {
    const next = await wasloApi.goLive(props.accountId);
    confirming.value = false;
    emit('changed', next);
  } catch {
    goError.value = t('WASLO.READINESS.GO_LIVE_FAILED');
  } finally {
    going.value = false;
  }
};

/** The status headline's "Start answering customers" opens this same confirm. */
defineExpose({ askGoLive });

const LINK_CLASS =
  'text-sm font-medium text-n-slate-12 underline underline-offset-2 hover:text-n-blue-11';
</script>

<template>
  <div class="flex flex-col gap-6" data-waslo-readiness>
    <SettingsHeader
      :heading="t('WASLO.READINESS.HEADING')"
      :description="t('WASLO.READINESS.DESCRIPTION')"
    />

    <ul
      class="flex flex-col w-full mb-0 list-none divide-y outline outline-1 -outline-offset-1 divide-n-weak outline-n-container rounded-xl bg-n-solid-2"
    >
      <li
        v-for="step in steps"
        :key="step.key"
        class="flex flex-col gap-2 px-5 py-4 sm:flex-row sm:items-center"
        :data-waslo-readiness-step="step.key"
        :data-done="step.done"
      >
        <span
          class="grid rounded-lg shrink-0 place-items-center size-8"
          :class="step.done ? 'bg-n-blue-3' : 'bg-n-alpha-2'"
          aria-hidden="true"
        >
          <span
            class="size-4"
            :class="
              step.done
                ? 'i-lucide-circle-check text-n-blue-10'
                : 'i-lucide-circle text-n-slate-10'
            "
          />
        </span>
        <div class="flex flex-col min-w-0 gap-0.5">
          <p class="mb-0 text-sm font-medium text-n-slate-12">
            {{ t(`WASLO.READINESS.STEPS.${step.copy}`) }}
          </p>
          <p class="mb-0 text-xs text-n-slate-11">
            {{
              step.done
                ? t('WASLO.READINESS.DONE')
                : t('WASLO.READINESS.NOT_DONE')
            }}
          </p>
        </div>
        <span class="flex flex-wrap gap-3 sm:ms-auto">
          <router-link
            v-if="step.draftTo && !step.done"
            :to="step.draftTo"
            :class="LINK_CLASS"
            data-waslo-readiness-first-draft
          >
            {{ t('WASLO.SETUP_AI.FIRST_DRAFT.OPEN') }}
          </router-link>
          <router-link
            v-if="step.to"
            :to="step.to"
            :class="LINK_CLASS"
            data-waslo-readiness-link
          >
            {{ t(`WASLO.READINESS.LINKS.${step.copy}`) }}
          </router-link>
        </span>
      </li>
    </ul>

    <TestNumbersEditor
      copy="WASLO.READINESS.TESTERS"
      :numbers="testers"
      :max-numbers="READINESS_MAX_TESTERS"
      :is-saving="savingTesters"
      is-live
      :save-error="testersError"
      @add="addTester"
      @remove="removeTester"
    />

    <div class="flex flex-col gap-3" aria-live="polite">
      <div v-if="!confirming">
        <Button
          :label="t('WASLO.STATUS.FIX.GO_LIVE')"
          icon="i-lucide-play"
          size="sm"
          data-waslo-go-live
          @click="askGoLive"
        />
      </div>
      <div
        v-else
        class="flex flex-col gap-3 p-4 outline outline-1 -outline-offset-1 outline-n-weak rounded-xl bg-n-solid-1"
        data-waslo-go-live-confirm
      >
        <p class="mb-0 text-sm leading-6 text-n-slate-12">{{ confirmLine }}</p>
        <div class="flex flex-wrap items-center gap-2">
          <Button
            :label="t('WASLO.READINESS.CONFIRM_YES')"
            size="sm"
            :is-loading="going"
            data-waslo-go-live-yes
            @click="goLive"
          />
          <Button
            :label="t('WASLO.READINESS.CONFIRM_NO')"
            size="sm"
            variant="faded"
            color="slate"
            data-waslo-go-live-no
            @click="confirming = false"
          />
        </div>
      </div>
      <p
        v-if="goError"
        class="mb-0 text-sm text-n-ruby-11"
        data-waslo-go-live-error
      >
        {{ goError }}
      </p>
    </div>
  </div>
</template>
